import Config from '@config';
import { normalizeInfiniteQueryData } from '@gasbi/shared/common/features/utils';
import { createApi } from '@reduxjs/toolkit/query/react';
import { useSelector } from 'react-redux';

import { CACHE_EXPIRATION_TIME } from '@/features/constants';
import { cachedItemByIdSelector, infiniteQueryOptions } from '@/features/utils';
import axiosBaseQuery from '@/utils/axiosBaseQuery';

import { FiltersGrid, Pagination } from '../types';
import {
  ConsumerCategories,
  Contract,
  ContractNumberRequestBody,
  ContractReferenceBook,
  ContractRequestBody,
  ContractViewModel,
} from './types';

export const contractsApi = createApi({
  reducerPath: 'contractsApi',
  baseQuery: axiosBaseQuery({
    baseUrl: Config.VITE_API_URL + `/contracts`,
  }),
  tagTypes: ['Contract', 'Contracts', 'ContractReferenceBook'],
  keepUnusedDataFor: CACHE_EXPIRATION_TIME,
  endpoints: builder => ({
    getContracts: builder.query<
      ContractViewModel[],
      FiltersGrid & { commercialMeteringPointSubdivisionId?: string }
    >({
      query: params => ({
        url: '/query',
        params,
      }),
      providesTags: () => ['Contract', 'Contracts'],
    }),
    getContractsI: builder.infiniteQuery<
      ContractViewModel[],
      FiltersGrid & { commercialMeteringPointSubdivisionId?: string },
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: '/query',
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: () => ['Contract', 'Contracts'],
    }),
    createContract: builder.mutation<Contract, ContractRequestBody>({
      query: data => ({
        url: '/',
        method: 'POST',
        data,
      }),
      invalidatesTags: result => [
        'Contract',
        { type: 'Contract', id: result?.id },
      ],
    }),
    updateContract: builder.mutation<Contract, ContractRequestBody>({
      query: data => ({
        url: `/${data.id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: result => [
        'Contract',
        { type: 'Contract', id: result?.id },
      ],
    }),
    getContractById: builder.query<Contract, string>({
      query: id => ({
        url: `/${id}`,
      }),
      providesTags: () => ['Contract', 'Contracts'],
    }),
    getContractReferenceBooks: builder.query<ContractReferenceBook, void>({
      query: () => ({
        url: `/reference-books`,
      }),
      providesTags: () => ['ContractReferenceBook'],
    }),
    getContractConsumerCategories: builder.query<ConsumerCategories[], void>({
      query: () => ({
        url: `/reference-books`,
      }),
      providesTags: () => ['ContractReferenceBook'],
      transformResponse: (response: ContractReferenceBook) =>
        response?.consumerCategories,
    }),
    updateContractNumberById: builder.mutation<
      Contract,
      ContractNumberRequestBody
    >({
      query: data => ({
        url: `/${data.id}/contract-number`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: result =>
        result ? ['Contract', { type: 'Contract', id: result?.id }] : [],
    }),
  }),
});

export const {
  useGetContractsQuery,
  useLazyGetContractsQuery,
  useGetContractReferenceBooksQuery,
  useCreateContractMutation,
  useUpdateContractMutation,
  useGetContractByIdQuery,
  useUpdateContractNumberByIdMutation,
  useGetContractConsumerCategoriesQuery,
} = contractsApi;

export const useGetContractsInfiniteQuery = normalizeInfiniteQueryData(
  contractsApi.useGetContractsIInfiniteQuery,
);

const getCachedContractByIdSelector = cachedItemByIdSelector(
  'contractsApi',
  'getContractsI',
);

export const useGetCachedContractByIdSelector = id =>
  useSelector(getCachedContractByIdSelector(id));
